import { agentExecutionInputAtomFamily } from "@/components/ai/chat/state";
import { Button } from "@/components/flexkit/Button";
import {
    Dialog,
    DialogClose,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from "@/components/podkit/dialog/Dialog";
import { useToast } from "@/components/podkit/toasts/use-toast";
import { TrackLocations } from "@/hooks/use-segment";
import { useDeleteAgentExecution, type PlainAgentExecution } from "@/queries/agent-queries";
import { getActiveEnvironmentId } from "@/utils/agent-executions";
import { formatError } from "@/utils/errors";
import { useSetAtom } from "jotai";
import { useCallback, type FC } from "react";

type DeleteAgentExecutionModalProps = {
    agentExecution: PlainAgentExecution;
    open: boolean;
    onClose: () => void;
    /**
     * Optional input value to preserve when resetting the conversation.
     * If provided, this value will be saved to the environment-based storage key
     * so it persists after the conversation is reset.
     */
    inputValue?: string;
};

export const DeleteAgentExecutionModal: FC<DeleteAgentExecutionModalProps> = ({
    agentExecution,
    open,
    onClose,
    inputValue,
}: DeleteAgentExecutionModalProps) => {
    const { toast } = useToast();
    const environmentId = getActiveEnvironmentId(agentExecution);
    const setEnvironmentInput = useSetAtom(agentExecutionInputAtomFamily(environmentId ?? ""));

    const deleteAgentExecution = useDeleteAgentExecution();

    const onOpenChange = (open: boolean) => {
        if (!open) {
            onClose();
        }
    };

    const handleDeleteAgentExecution = useCallback(async () => {
        // Preserve the input value using the Jotai atom so it syncs properly
        if (environmentId && inputValue) {
            setEnvironmentInput(inputValue);
        }

        await deleteAgentExecution.mutateAsync(
            {
                agentExecutionId: agentExecution.id,
            },
            {
                onSuccess: () => {
                    onClose();
                },
                onError: (e) => {
                    toast({
                        title: "Failed to delete conversation",
                        description: formatError(e),
                    });
                },
            },
        );
    }, [deleteAgentExecution, agentExecution.id, onClose, toast, inputValue, environmentId, setEnvironmentInput]);

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent
                className="max-w-xl"
                data-track-location={TrackLocations.DeleteAgentExecutionModal}
                data-testid="delete-agent-execution-modal"
            >
                <DialogHeader>
                    <DialogTitle>Reset conversation</DialogTitle>
                    <DialogDescription>
                        This will clear all messages in the conversation. Are you sure you want to continue?
                    </DialogDescription>
                </DialogHeader>

                <DialogFooter>
                    <DialogClose asChild>
                        <Button
                            type="button"
                            variant="outline"
                            onClick={onClose}
                            data-tracking-id="cancel-delete-agent-execution-modal"
                        >
                            Cancel
                        </Button>
                    </DialogClose>
                    <Button
                        autoFocus={true}
                        variant="destructive"
                        loading={deleteAgentExecution.isPending}
                        onClick={handleDeleteAgentExecution}
                        data-testid="delete-agent-execution-modal-delete-button"
                        data-tracking-id="delete-agent-execution-delete-agent-execution-modal"
                    >
                        Yes, Reset
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
};
